import { ILinkedServiceTypeMetadata } from 'app/extensibility/metadatas/resource-type/linked-service-type-metadata';

import { concurAuthType } from 'app/model/linkedservice/concur.linkedservice';
import { Metamodel } from 'app/model/base/model';
import { ConcurCopySource } from 'app/model/pipeline/copysource/concur.copysource';
import { useEncryptedEndpointsRadioSwitch } from 'app/registries/linked-service/linked-service-shared-fields';
import { format } from 'app/shared/utils/string.util';
import { adfModelTypeMap } from 'app/registries/consts/adf-model-type-map-const';
import { concurReservedKeys } from 'app/shared/utils/connector-metadata-static-consts';
import { deprecatedConnectorDocLink } from 'app/shared/constants/links.const';

export const concurMetadata: ILinkedServiceTypeMetadata = {
    type: adfModelTypeMap.concur.linkedServiceType,
    displayName: format(stringResources.ModelName_DisplayTextLegacyTemplate, stringResources.Concur_DisplayText),
    linkedServiceKind: 'table',
    icon: {
        className: 'svg-i-100x100-concur'
    },
    warningMessage: stringResources.Connector_Legacy_IsDeprecatedWarning,
    warningMessageHyperlink: deprecatedConnectorDocLink,
    warningTooltip: stringResources.Connector_Legacy_IsDeprecatedHint,
    isFirstClass: true,
    category: ['ServicesApps'],
    supportDatasetV2: true,
    notShowInTypeSelection: true,
    copySinkTypes: [],
    copySourceTypes: [Metamodel.modelFor(ConcurCopySource).name],
    controls: [
        {
            type: 'textbox',
            bind: 'clientId',
            label: stringResources.ModelName_ClientId,
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            hide: ('{ !isOldResource }' as any) as boolean, // it's an expression and will be evaluated into boolean-type value in control factory service
            required: true,
            validators: [
                { type: 'required', errorMessage: '' }
            ],
            isLabelVertical: true
        },
        {
            type: 'textbox',
            bind: 'username',
            label: stringResources.ModelName_UserName,
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            hide: ('{ !isOldResource }' as any) as boolean, // it's an expression and will be evaluated into boolean-type value in control factory service
            required: true,
            validators: [
                { type: 'required', errorMessage: '' }
            ],
            isLabelVertical: true
        },
        {
            type: 'dynamic-akv-switcher',
            bind: 'password',
            label: stringResources.ModelName_Password,
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            hide: ('{ !isOldResource }' as any) as boolean, // it's an expression and will be evaluated into boolean-type value in control factory service
            child: {
                type: 'password',
                label: stringResources.ModelName_Password,
                required: true,
                isLabelVertical: true
            },
            validators: [
                { type: 'required', errorMessage: '' }
            ],
            description: format(stringResources.AzureKeyVaultSecretName_Tooltip_Template,
                format(stringResources.ModelName_LinkedServiceTemplate, stringResources.Concur_DisplayText),
                stringResources.ModelName_Password)
        },
        {
            type: 'select-switch',
            bind: 'authType',
            label: stringResources.ModelName_AuthenticationType,
            hide: '{ isOldResource }',
            options: [
                {
                    label: concurAuthType.oldAuthVersion,
                    value: concurAuthType.oldAuthVersion
                },
                {
                    label: concurAuthType.newAuthVersion,
                    value: concurAuthType.newAuthVersion
                }
            ],
            isLabelVertical: true,
            defaultValue: concurAuthType.newAuthVersion,
            cases: [
                {
                    caseValue: concurAuthType.oldAuthVersion,
                    children: [
                        {
                            type: 'textbox',
                            bind: 'host',
                            label: stringResources.ModelName_Host,
                            required: true,
                            placeholder: stringResources.Concur_LinkedService_HttpPath_Placeholder,
                            validators: [
                                { type: 'required', errorMessage: '' }
                            ],
                            isLabelVertical: true
                        },
                        {
                            type: 'textbox',
                            bind: 'clientId',
                            label: stringResources.ModelName_ClientId,
                            required: true,
                            validators: [
                                { type: 'required', errorMessage: '' }
                            ],
                            isLabelVertical: true
                        },
                        {
                            type: 'textbox',
                            bind: 'username',
                            label: stringResources.ModelName_UserName,
                            required: true,
                            validators: [
                                { type: 'required', errorMessage: '' }
                            ],
                            isLabelVertical: true
                        },
                        {
                            type: 'dynamic-akv-switcher',
                            bind: 'password',
                            label: stringResources.ModelName_Password,
                            child: {
                                type: 'password',
                                label: stringResources.ModelName_Password,
                                required: true,
                                isLabelVertical: true
                            },
                            validators: [
                                { type: 'required', errorMessage: '' }
                            ],
                            description: format(stringResources.AzureKeyVaultSecretName_Tooltip_Template,
                                format(stringResources.ModelName_LinkedServiceTemplate, stringResources.Concur_DisplayText),
                                stringResources.ModelName_Password)
                        }
                    ]
                },
                {
                    caseValue: concurAuthType.newAuthVersion,
                    children: [
                        {
                            type: 'textbox',
                            bind: 'host',
                            label: stringResources.ModelName_Host,
                            required: true,
                            placeholder: stringResources.Concur_LinkedService_HttpPath_Placeholder,
                            validators: [
                                { type: 'required', errorMessage: '' }
                            ],
                            isLabelVertical: true
                        },
                        {
                            type: 'textbox',
                            bind: 'baseUrl',
                            label: stringResources.ModelName_BaseUrl,
                            required: true,
                            validators: [
                                { type: 'required', errorMessage: '' }
                            ],
                            isLabelVertical: true
                        },
                        {
                            type: 'textbox',
                            bind: 'clientId',
                            label: stringResources.ModelName_ClientId,
                            required: true,
                            validators: [
                                { type: 'required', errorMessage: '' }
                            ],
                            isLabelVertical: true
                        },
                        {
                            type: 'dynamic-akv-switcher',
                            bind: 'clientSecret',
                            label: stringResources.ModelName_ClientSecret,
                            child: {
                                type: 'password',
                                label: stringResources.ModelName_ClientSecret,
                                required: true,
                                isLabelVertical: true
                            },
                            validators: [
                                { type: 'required', errorMessage: '' }
                            ]
                        },
                        {
                            type: 'textbox',
                            bind: 'username',
                            label: stringResources.ModelName_UserName,
                            required: true,
                            validators: [
                                { type: 'required', errorMessage: '' }
                            ],
                            isLabelVertical: true
                        },
                        {
                            type: 'dynamic-akv-switcher',
                            bind: 'password',
                            label: stringResources.ModelName_Password,
                            child: {
                                type: 'password',
                                label: stringResources.ModelName_Password,
                                required: true,
                                isLabelVertical: true
                            },
                            validators: [
                                { type: 'required', errorMessage: '' }
                            ],
                            description: format(stringResources.AzureKeyVaultSecretName_Tooltip_Template,
                                format(stringResources.ModelName_LinkedServiceTemplate, stringResources.Concur_DisplayText),
                                stringResources.ModelName_Password)
                        }
                    ]
                }
            ]
        },
        useEncryptedEndpointsRadioSwitch,
        {
            type: 'key-val-list',
            bind: 'additionalFields',
            label: stringResources.AdditionalConnectionStringFields,
            hide: '{ isOldResource }',
            reservedKeys: concurReservedKeys
        }
    ]
};
